import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { TooltipProvider } from '@/components/ui';
import { X } from 'lucide-react';
import { useUIStore, useDesignStore } from '@/stores';
import { cn } from '@/lib/utils';
import { Header } from './Header';
import { LeftToolbar } from './LeftToolbar';
import { BottomBar } from './BottomBar';
import { ProductGrid, ProductsStepLayout } from '@/components/products';
import { PublishLayout } from '@/components/publish';
import { GuidedDesignStep } from '@/components/creator';
import { TextPropertiesBar } from '@/components/editor/TextPropertiesBar';
import { ImagePropertiesBar } from '@/components/editor/ImagePropertiesBar';
import { LayersPanel } from '@/components/editor/LayersPanel';
import { ProductPreview } from '@/components/editor/ProductPreview';
import { ProductAdjustmentView } from '@/components/editor/ProductAdjustmentView';
import { StepHint } from '@/components/creator/StepHint';
import { isSystemLayer } from '@/types/canvas';

/**
 * Full-height shell styles that clear the fixed impersonation banner
 * (components/layout/ImpersonationBanner.tsx). Outside an impersonation tab
 * the variable is absent and the 0px fallback gives 100vh and no offset.
 */
const CREATOR_SHELL_STYLE = {
  height: 'calc(100vh - var(--impersonation-banner-height, 0px))',
  marginTop: 'var(--impersonation-banner-height, 0px)',
} as const;
const FULLSCREEN_EDITOR_STYLE = {
  top: 'var(--impersonation-banner-height, 0px)',
} as const;

/**
 * MainLayout component - Prototype-style layout
 *
 * Layout structure (like JUNIQE/Prototype):
 * ┌─────────────────────────────────────────────────────────────────────────┐
 * │                          STICKY HEADER                                  │
 * │  [← Back] [Logo]  [1. Design] [2. Products] [3. Publish]  [Save][Exit] │
 * ├────────┬─────────────────────────────────────┬──────────────────────────┤
 * │        │                                     │                          │
 * │  LEFT  │                                     │     RIGHT SIDEBAR        │
 * │  TOOL  │         CENTER PREVIEW              │       (50% width)        │
 * │  BAR   │        (Canvas/Mockup)              │                          │
 * │        │                                     │   - Stats Header         │
 * │  16px  │         bg-canvas                   │   - Category Tabs        │
 * │        │         #E8E4F0                     │   - Product Grid (3 col) │
 * │        ├─────────────────────────────────────┤   - Footer Summary       │
 * │        │          BOTTOM BAR                 │                          │
 * │        │  [Front][Back] [3:4] [Zoom]         │                          │
 * └────────┴─────────────────────────────────────┴──────────────────────────┘
 */
export function MainLayout() {
  const { t } = useTranslation();
  const currentStep = useUIStore((state) => state.currentStep);
  const creatorEntryMode = useUIStore((state) => state.creatorEntryMode);
  const editorMode = useUIStore((state) => state.editorMode);
  const singleProductMode = useUIStore((state) => state.singleProductMode);
  const isFullscreenEditor = useUIStore((state) => state.isFullscreenEditor);
  const setFullscreenEditor = useUIStore((state) => state.setFullscreenEditor);
  const selectedObjects = useDesignStore((state) => state.selectedObjects);
  const [isLayersPanelOpen, setIsLayersPanelOpen] = useState(false);

  // Exit fullscreen on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isFullscreenEditor) {
        setFullscreenEditor(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreenEditor, setFullscreenEditor]);

  // Check if a text object is selected
  const hasTextSelected = selectedObjects.some(
    (obj) =>
      obj.type === 'i-text' || obj.type === 'text' || obj.type === 'textbox'
  );

  // Check if an image object is selected (not a system layer)
  const hasImageSelected = selectedObjects.some(
    (obj) => obj.type === 'image' && !isSystemLayer(obj.name)
  );

  return (
    <TooltipProvider delayDuration={300}>
      <div
        className="creator-shell flex flex-col bg-[var(--paper)] overflow-hidden"
        // A full viewport minus the impersonation banner, pushed below it, so
        // the bottom bar stays visible.
        style={CREATOR_SHELL_STYLE}
      >
        {/* Sticky Header */}
        <Header />
        <StepHint />

        {currentStep === 'catalog' ? (
          <ProductsStepLayout mode="catalog" />
        ) : currentStep === 'products' ? (
          <ProductsStepLayout
            mode={creatorEntryMode === 'guided' ? 'overview' : 'default'}
          />
        ) : currentStep === 'publish' ? (
          <PublishLayout />
        ) : creatorEntryMode === 'guided' && !singleProductMode ? (
          /* Guided entry replaces the canvas editor on step 1 only. Free
             placement happens inside the cards, so this branch holds for the
             whole step. The catalogue exists only in the guided entry; step 3
             is shared with the classic entry, and so is "Platzierung
             anpassen" on a product, which forces the design step in
             single-product mode and needs the real editor. */
          <GuidedDesignStep />
        ) : (
          /* Main Content Area */
          <main
            className={cn(
              'flex-1 flex overflow-hidden relative',
              isFullscreenEditor && 'fixed inset-0 z-50 bg-[var(--paper)]'
            )}
            style={isFullscreenEditor ? FULLSCREEN_EDITOR_STYLE : undefined}
          >
            {/* Exit fullscreen button (top-right corner) */}
            {isFullscreenEditor && (
              <button
                onClick={() => setFullscreenEditor(false)}
                title={t('creator.toolbar.exitFullscreenEsc')}
                className="absolute top-3 right-3 z-10 p-1.5 bg-[var(--paper)] rounded-control ring-1 ring-inset ring-[var(--line)] hover:bg-[var(--paper-2)] text-[var(--ink-2)] transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            )}

            {/* Left Toolbar - only visible in Design step */}
            {currentStep === 'design' && (
              <LeftToolbar
                onLayersPanelToggle={() =>
                  setIsLayersPanelOpen(!isLayersPanelOpen)
                }
                isLayersPanelOpen={isLayersPanelOpen}
              />
            )}

            {/* Layers Panel - Flyout from left toolbar */}
            {currentStep === 'design' && (
              <LayersPanel
                isOpen={isLayersPanelOpen}
                onClose={() => setIsLayersPanelOpen(false)}
              />
            )}

            {/* Center Preview Area - 5/9 of the screen (full width in fullscreen) */}
            <section className="flex-[5] flex flex-col overflow-hidden relative">
              {/* Stufe 5.3: Product Adjustment Mode (rendered on top when active) */}
              {editorMode.type === 'product-adjust' && (
                <ProductAdjustmentView productId={editorMode.productId} />
              )}

              {/* Canvas Container with Full-Mockup CanvasEditor
                  CRITICAL: Always rendered, hidden via CSS when in product-adjust mode.
                  This preserves the Fabric.js canvas instance and avoids reinitialization. */}
              <div
                className={cn(
                  'flex-1 flex flex-col min-h-0',
                  editorMode.type === 'product-adjust' && 'hidden'
                )}
              >
                <ProductPreview />
              </div>

              {/* Bottom Bar + Property Bars: only visible in normal design mode */}
              {editorMode.type !== 'product-adjust' && (
                <>
                  <BottomBar />

                  {/* Text Properties Bar - Bottom bar when text is selected */}
                  {currentStep === 'design' && hasTextSelected && (
                    <TextPropertiesBar />
                  )}

                  {/* Image Properties Bar - Bottom bar when image is selected */}
                  {currentStep === 'design' &&
                    hasImageSelected &&
                    !hasTextSelected && <ImagePropertiesBar />}
                </>
              )}
            </section>

            {/* Right Sidebar - hidden in fullscreen or single-product mode */}
            {!singleProductMode && !isFullscreenEditor && <ProductGrid />}
          </main>
        )}
      </div>
    </TooltipProvider>
  );
}
